import { computed, h, defineComponent, ref } from 'vue';
import { ResourceTable, ResourceTableProps, resourceTableProps } from '@/ui/components/table/ResourceTable';
import { column, textColumn } from '@/ui/components';
import { d, t } from '@/ui/i18n/i18n';
import { manageIssueModal } from '@/ui/components/issue/ManageIssuesModal';
import { Question } from '@/entities/question';
import { IssueType } from '@/entities/issue';
import { manageIssuesButton } from '@/ui/components/issue/ManageIssuesModal/ManageIssuesButton';
import { SortDirection } from '@/ui/types';
import { router } from '@/ui/routes';
import { InspectionDetailSections } from '@/ui/routes/types';

interface InspectionNegativeAnswerListProps<T = unknown> extends ResourceTableProps<T> {
  records: Question[];
  loading: boolean;
}

export const InspectionNegativeAnswerList = defineComponent<InspectionNegativeAnswerListProps>({
  name: 'InspectionNegativeAnswerList',
  props: [...resourceTableProps, 'records', 'loading'],
  setup(props, context) {
    const isModalOpen = ref(false);

    const selectedNegativeAnswer = ref<Question | null>(null);

    const openModal = (negativeAnswer: Question) => {
      selectedNegativeAnswer.value = negativeAnswer;
      isModalOpen.value = true;
    };

    const closeModal = () => {
      isModalOpen.value = false;
      selectedNegativeAnswer.value = null;
    };

    const onRecordCreated = () => {
      isModalOpen.value = false;
      selectedNegativeAnswer.value = null;
      context.emit('issue:created');
    };

    const mergeAttributes = computed(() => {
      if (!selectedNegativeAnswer.value) {
        return {};
      }

      return { question: selectedNegativeAnswer.value.id, type: IssueType.AdHoc };
    });

    return function render() {
      return h('div', [
        isModalOpen.value
          ? manageIssueModal({
            props: {
              active: isModalOpen.value,
              mergeAttributes: mergeAttributes.value,
              issues: (selectedNegativeAnswer.value && selectedNegativeAnswer.value.issues)
                ? selectedNegativeAnswer.value.issues
                : [],
            },
            on: {
              close: closeModal,
              'issue:created': onRecordCreated,
            },
          }) : null,
        h(ResourceTable, {
          props: {
            ...props,
            clickableTitle: false,
            list: props.records,
          },
          on: {
            sort: (field: string, direction: SortDirection) => context.emit('sort', field, direction),
            click: (row : Question) => {
              router.push({
                name: 'inspectionDetailSection',
                params: {
                  id: `${row.inspectionId}`,
                  section: InspectionDetailSections.NEGATIVE_ANSWERS,
                },
              });
            },
          },
          scopedSlots: {
            default: () => [
              textColumn(
                {
                  field: 'conductedAt',
                  label: t('checklist.conducted-at'),
                  sortable: true,
                }, {
                  default: ({ row: inspection }: { row: Question }) =>
                    inspection.conductedAt ? d(inspection.conductedAt) : null,
                },
              ),
              column(
                {
                  field: 'query',
                  label: t('inspections.query'),
                  cellClass: 'table__title-cell',
                }, {
                  default: ({ row: inspection }: { row: Question }) =>
                    inspection.title,
                },
              ),
              column(
                {
                  field: 'response',
                  label: t('common.response'),
                }, {
                  default: ({ row: inspection }: { row: Question }) =>
                    inspection.structuredResponse?.map(response => response.title).join(', '),
                },
              ),
              column(
                {
                  field: 'formTitle',
                  label: t('inspections.title'),
                  sortable: true,
                  cellClass: 'table__title-cell',
                }, {
                  default: ({ row: inspection }: { row: Question }) =>
                    [inspection.inspectionTitle || inspection.checklistTitle],
                },
              ),
              textColumn({
                field: 'user',
                label: t('checklist.conducted-by'),
                sortable: true,
              }, {
                default: ({ row: inspection }: { row: Question }) =>
                  inspection.conductedBy ? inspection.conductedBy : null,
              }),
              textColumn(
                {
                  field: 'entityItem',
                  label: t('common.entity-item'),
                  sortable: true,
                }, {
                  default: ({ row: inspection }: { row: Question }) =>
                    inspection.entityItem ? inspection.entityItem : null,
                },
              ),
              column({
                field: 'actions',
                label: t('common.issue'),
              }, {
                default: ({ row: inspection }: { row: Question }) => manageIssuesButton({
                  props: { issues: inspection.issues ?? [] },
                  on: { click: () => openModal(inspection) },
                }),
              }),
            ],
          },
        }),
      ]);
    };
  },
});
